import React, {
  useRef,
  useState,
  useMemo,
  type PropsWithChildren,
  type FunctionComponent,
} from 'react'
import {
  Modal,
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  NotificationLevel,
  Text,
  notify,
  Button,
  spacing,
} from '@retailer-platform/shared-components'
import { IconButton } from '@instacart/ids-customers'
import { ConfirmIcon } from '@instacart/ids-core'
import faker from 'faker'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import {
  AttributeManagementCategoryEnum,
  type AttributeManagementStepVersion,
  AttributeManagementTypeEnum,
  graphQLStepVersionToAttributeManagementStepVersion,
  type PipelineStep,
} from '../../../types/attributeManagement.types'
import { useDomainMessages } from '../../../utils/domain/intl'
import AttributeManagementList from '../../attribute-management-list/AttributeManagementList'
import { useGetListStepVersionsQuery } from '../../../api'
import { ButtonRow } from '../../shared-styled-components/SharedStyledComponents'
import { PipelineFormStepVersionSelectTable } from './PipelineFormStepVersionSelectTable'
import StepAttributeForm from './StepAttributeForm'
interface Props {
  category: AttributeManagementCategoryEnum | null
  onClose: () => void
  onConfirm: (step: PipelineStep) => void
}

interface State {
  category: AttributeManagementCategoryEnum | null
  selectedStepId: string | null
  selectedStepVersionId: string | null
  baseAttributes: any | null
  overrideAttributes: any | null
  type: AttributeManagementTypeEnum | null
  version: string | null
}

const DEFAULT_STATE: State = {
  category: null,
  selectedStepId: null,
  selectedStepVersionId: null,
  baseAttributes: null,
  overrideAttributes: null,
  type: null,
  version: null,
}

const ExplanationText = styled.div({
  marginBottom: spacing.X24,
})

const EXCLUDED_STEP_CATEOGRIES = ['Unknown', 'Input']

const PipelineFormStepSelectModal: React.FC<Props> = ({ onClose, onConfirm, category }) => {
  const i18n = useDomainMessages({
    selectStepTitle:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.selectStepTitle',
    selectStepVersionTitle:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.selectStepVersionTitle',
    reset:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.reset',
    restart:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.restart',
    confirm:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.confirm',
    setOverridesTitle:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.setOverridesTitle',
    selectStepDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.selectStepDescription',
    selectStepVersionDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.selectStepVersionDescription',
    setOverridesDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.setOverridesDescription',
    cancel: 'catalogAdminDomain.attributeManagement.runTests.modal.button.cancel',
  })
  const [state, setState] = useState<State>({ ...DEFAULT_STATE, category })

  const confirm = React.useCallback(() => {
    if (
      !state.baseAttributes ||
      !state.overrideAttributes ||
      !state.selectedStepVersionId ||
      !state.version ||
      !state.selectedStepId
    ) {
      return
    }

    // Create an object with only the keys that have different values from baseAttributes
    const attributeOverrides = Object.keys(state.overrideAttributes).reduce((acc, key) => {
      // Only include keys where values differ
      if (
        JSON.stringify(state.overrideAttributes[key]) !== JSON.stringify(state.baseAttributes[key])
      ) {
        acc[key] = state.overrideAttributes[key]
      }
      return acc
    }, {} as Record<string, any>)

    onConfirm({
      stepVersionId: state.selectedStepVersionId,
      stepId: state.selectedStepId,
      version: state.version,
      attributeOverrides,
    })
  }, [
    onConfirm,
    state.baseAttributes,
    state.overrideAttributes,
    state.selectedStepVersionId,
    state.selectedStepId,
    state.version,
  ])

  return (
    <Modal
      isOpen={true}
      maxWidth={'90%'}
      maxHeight={'90%'}
      onRequestClose={onClose}
      styleOverrides={{
        content: {
          height: '90vh',
        },
      }}
    >
      <ModalContainer>
        <ModalHeader>
          {(state.selectedStepVersionId && i18n.setOverridesTitle) ||
            (state.selectedStepId && i18n.selectStepVersionTitle) ||
            (state.category && i18n.selectStepTitle) ||
            'Select a category'}
        </ModalHeader>
        {!state.category && (
          <SelectCategoryModalContent onSelect={category => setState({ ...state, category })} />
        )}
        {state.category && !state.selectedStepId && (
          <StepSelectModalContent
            category={state.category}
            onSelect={stepId => setState({ ...state, selectedStepId: stepId })}
          />
        )}
        {state.selectedStepId && !state.selectedStepVersionId && (
          <StepVersionSelectModalContent
            stepId={state.selectedStepId}
            onSelect={(stepVersionId, attributes, type, version) => {
              const overrideAttributes = attributes
              setState({
                ...state,
                selectedStepVersionId: stepVersionId,
                overrideAttributes: overrideAttributes,
                baseAttributes: attributes,
                type,
                version,
              })
            }}
          />
        )}
        {state.selectedStepId && state.selectedStepVersionId && (
          <OverrideSelectionModalContent
            stepVersion={state.selectedStepVersionId}
            type={state.type || AttributeManagementTypeEnum.LLMPromptTemplate}
            attributes={state.overrideAttributes}
            onChange={attributes => setState({ ...state, overrideAttributes: attributes })}
          />
        )}
        <ModalFooter>
          <ButtonRow>
            <Button variant={'secondary'} onClick={onClose}>
              {i18n.cancel}
            </Button>
            <Button
              variant={'secondary'}
              disabled={!state.selectedStepId}
              onClick={() => setState(DEFAULT_STATE)}
            >
              {i18n.restart}
            </Button>
            <Button
              variant={'secondary'}
              disabled={
                !state.selectedStepVersionId ||
                JSON.stringify(state.overrideAttributes) === JSON.stringify(state.baseAttributes)
              }
              onClick={() => setState({ ...state, overrideAttributes: state.baseAttributes })}
            >
              {i18n.reset}
            </Button>
            <Button variant={'primary'} disabled={!state.selectedStepVersionId} onClick={confirm}>
              {i18n.confirm}
            </Button>
          </ButtonRow>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  )
}

interface OverrideSelectionProps {
  type: AttributeManagementTypeEnum
  stepVersion: string
  attributes: any
  onChange: (attributes: any) => void
}

const OverrideSelectionModalContent = ({
  stepVersion,
  type,
  attributes,
  onChange,
}: OverrideSelectionProps) => {
  const i18n = useDomainMessages({
    setOverridesDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.setOverridesDescription',
  })
  return (
    <ModalContent explanation={i18n.setOverridesDescription}>
      <StepAttributeForm
        type={type}
        attributes={attributes}
        mode={'override'}
        onChange={attributes => onChange(attributes)}
        version={parseInt(stepVersion)}
      />
    </ModalContent>
  )
}
interface StepVersionSelectProps {
  stepId: string
  onSelect: (
    stepVersionId: string,
    attributes: any,
    type: AttributeManagementTypeEnum,
    version: string
  ) => void
}

const StepVersionSelectModalContent = ({ stepId, onSelect }: StepVersionSelectProps) => {
  const i18n = useDomainMessages({
    selectStepVersionDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.selectStepVersionDescription',
  })
  const idempotencyKey = useRef(faker.random.uuid())
  const { data, loading } = useGetListStepVersionsQuery({
    variables: {
      clientId: 'catalog_camp_ipp',
      idempotencyKey: idempotencyKey.current,
      stepId: stepId,
    },
    onError: (error: any) => {
      notify({
        contents: `Error fetching steps: ${error.message}`,
        level: NotificationLevel.Error,
      })
    },
  })
  const versions: AttributeManagementStepVersion[] = useMemo(
    () =>
      data?.instacart_catalog_catflow_v1_CatflowAPI_ListStepVersions?.stepVersions
        ?.map(item => graphQLStepVersionToAttributeManagementStepVersion(item))
        ?.sort((a, b) => parseInt(b.version ?? '0') - parseInt(a.version ?? '0')) || [],
    [data]
  )
  return (
    <ModalContent explanation={i18n.selectStepVersionDescription}>
      <Text size="large" weight="semibold" styles={css({ textAlign: 'center' })}>
        {
          data?.instacart_catalog_catflow_v1_CatflowAPI_ListStepVersions?.stepVersions?.[0]?.step
            ?.name
        }
      </Text>
      <PipelineFormStepVersionSelectTable
        listData={versions}
        loading={loading}
        onSelect={stepVersionId =>
          onSelect(
            stepVersionId,
            versions.find(v => v.id === stepVersionId)?.attributes,
            AttributeManagementTypeEnum[
              (data?.instacart_catalog_catflow_v1_CatflowAPI_ListStepVersions?.stepVersions?.[0]
                ?.step?.type || 'LLMPromptTemplate') as keyof typeof AttributeManagementTypeEnum
            ],
            versions.find(v => v.id === stepVersionId)?.version || ''
          )
        }
      />
    </ModalContent>
  )
}
interface StepSelectProps {
  category: AttributeManagementCategoryEnum
  onSelect: (stepId: string) => void
}

const StepSelectModalContent = ({ category, onSelect }: StepSelectProps) => {
  const i18n = useDomainMessages({
    selectStepDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.selectStepDescription',
    selectStepTitle:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.selectStepTitle',
  })
  const action = React.useCallback(
    (stepId: string) => (
      <IconButton
        icon={ConfirmIcon}
        accessibleLabel={i18n.selectStepTitle}
        onPress={() => onSelect(stepId)}
      />
    ),
    [i18n.selectStepTitle, onSelect]
  )
  return (
    <ModalContent explanation={i18n.selectStepDescription}>
      <AttributeManagementList category={category} hideCreateButton actions={[action]} />
    </ModalContent>
  )
}
interface CategorySelectProps {
  onSelect: (category: AttributeManagementCategoryEnum) => void
}

const SelectCategoryModalContent = ({ onSelect }: CategorySelectProps) => (
  <ModalContent explanation="Select a category of step to add">
    <div>
      {Object.keys(AttributeManagementCategoryEnum)
        .filter(category => !EXCLUDED_STEP_CATEOGRIES.includes(category))
        .map(category => (
          <Button
            key={category}
            onClick={() =>
              onSelect(
                AttributeManagementCategoryEnum[
                  category as keyof typeof AttributeManagementCategoryEnum
                ]
              )
            }
          >
            {category}
          </Button>
        ))}
    </div>
  </ModalContent>
)

interface ModalContentProps {
  explanation: string
}

const ModalContent: FunctionComponent<PropsWithChildren<ModalContentProps>> = ({
  explanation,
  children,
}) => (
  <ModalBody>
    <ExplanationText>
      <Text size="small" weight="semibold">
        {explanation}
      </Text>
    </ExplanationText>
    {children}
  </ModalBody>
)
export default PipelineFormStepSelectModal
